import type { Locale } from "./i18n";

interface AiExperienceContent {
  readonly eyebrow: string;
  readonly title: string;
  readonly body: string;
  readonly primaryAction: string;
  readonly secondaryAction: string;
  readonly proof: readonly [string, string, string];
  readonly previewLabel: string;
  readonly previewStatus: string;
  readonly rooms: readonly [string, string, string, string];
  readonly sampleEyebrow: string;
  readonly sampleTitle: string;
  readonly sampleBody: string;
  readonly flowEyebrow: string;
  readonly flowTitle: string;
  readonly flowBody: string;
  readonly flowTitles: readonly [string, string, string];
  readonly flowDescriptions: readonly [string, string, string];
  readonly navStudio: string;
  readonly navSamples: string;
  readonly navProfessionals: string;
  readonly navWorkspace: string;
  readonly navMaterials: string;
}

const english: AiExperienceContent = {
  eyebrow: "3D INTERIOR PLANNING FOR THAILAND",
  title: "Upload your floor plan.\nExplore your space in 3D.",
  body: "Prioritised in this order: shops, offices, condos, then townhouses. Confirm real dimensions and review the space in interactive 3D before meeting a professional.",
  primaryAction: "Open 3D structure preview",
  secondaryAction: "Explore Thai designs",
  proof: ["PDF and image plans", "Interactive 2D and 3D", "Private project storage"],
  previewLabel: "INTERACTIVE SPACE PREVIEW", previewStatus: "Dimensions confirmed",
  rooms: ["Living", "Bedroom", "Kitchen", "Bath"],
  sampleEyebrow: "DESIGN LIBRARY", sampleTitle: "Ideas ordered around Decobaan's core spaces", sampleBody: "Explore shops first, followed by offices, condos and townhouses.",
  flowEyebrow: "FROM PLAN TO SPACE", flowTitle: "A clearer start to an interior project", flowBody: "Visualise the structure first, then continue with professionals and project management.",
  flowTitles: ["Upload a plan", "Confirm dimensions", "Explore in 3D"],
  flowDescriptions: ["Use a PDF, JPG, PNG, or WebP floor plan.", "Check the property type, width, depth, ceiling, and layout.", "Rotate the model and save the original plan privately to your project."],
  navStudio: "3D plan preview", navSamples: "Designs", navProfessionals: "Professionals", navWorkspace: "My project", navMaterials: "Materials"
};

export const aiExperienceCopy: Readonly<Record<Locale, AiExperienceContent>> = {
  th: {
    eyebrow: "เว็บแอปวางแผนอินทีเรียร์ 3D สำหรับประเทศไทย",
    title: "อัปโหลดแบบแปลน\nแล้วสำรวจพื้นที่ของคุณใน 3D",
    body: "จัดลำดับการออกแบบเป็น ร้านค้า สำนักงาน คอนโด และทาวน์เฮาส์ ตรวจสอบขนาดจริงและดูพื้นที่ในโมเดล 3D ก่อนคุยกับผู้เชี่ยวชาญ",
    primaryAction: "เปิดดูโครงสร้าง 3D", secondaryAction: "ดูตัวอย่างดีไซน์ในไทย",
    proof: ["รองรับ PDF และไฟล์ภาพ", "ตรวจสอบทั้ง 2D และ 3D", "จัดเก็บในโปรเจกต์ส่วนตัว"],
    previewLabel: "ตัวอย่างพื้นที่แบบโต้ตอบ", previewStatus: "ยืนยันขนาดแล้ว",
    rooms: ["ห้องนั่งเล่น", "ห้องนอน", "ห้องครัว", "ห้องน้ำ"],
    sampleEyebrow: "คลังดีไซน์", sampleTitle: "ไอเดียตามลำดับพื้นที่หลักของ Decobaan", sampleBody: "ดูร้านค้าก่อน ตามด้วยสำนักงาน คอนโด และทาวน์เฮาส์",
    flowEyebrow: "จากแบบแปลนสู่พื้นที่", flowTitle: "เริ่มโปรเจกต์อินทีเรียร์ให้เห็นภาพชัดเจนขึ้น", flowBody: "ตรวจสอบโครงสร้างก่อน แล้วดำเนินโครงการร่วมกับผู้เชี่ยวชาญ",
    flowTitles: ["อัปโหลดแบบแปลน", "ยืนยันขนาดจริง", "สำรวจพื้นที่ใน 3D"],
    flowDescriptions: ["ใช้แบบแปลน PDF, JPG, PNG หรือ WebP", "ตรวจสอบประเภทอาคาร ความกว้าง ความยาว เพดาน และรูปแบบพื้นที่", "หมุนดูโมเดลและบันทึกแบบต้นฉบับไว้ในโปรเจกต์ส่วนตัว"],
    navStudio: "ตัวอย่างโครงสร้าง 3D", navSamples: "ตัวอย่างดีไซน์", navProfessionals: "ผู้เชี่ยวชาญ", navWorkspace: "โปรเจกต์ของฉัน", navMaterials: "วัสดุและสินค้า"
  },
  ko: {
    eyebrow: "태국 인테리어 3D 설계 웹앱", title: "도면을 올리고\n내 공간을 3D로 확인하세요", body: "상가, 오피스, 콘도, 타운하우스 순으로 우선 설계합니다. 실제 치수를 확인하고 전문가 상담 전 공간 구조를 3D로 검토합니다.",
    primaryAction: "3D 구조 미리보기 열기", secondaryAction: "태국 디자인 둘러보기", proof: ["PDF·이미지 도면 지원", "2D·3D 동시 확인", "프로젝트 비공개 저장"],
    previewLabel: "인터랙티브 공간 미리보기", previewStatus: "치수 확인 완료", rooms: ["거실", "침실", "주방", "욕실"],
    sampleEyebrow: "디자인 라이브러리", sampleTitle: "데코반 핵심 공간 순서로 보는 인테리어 아이디어", sampleBody: "상가를 먼저 보고 오피스, 콘도, 타운하우스 사례로 이어집니다.",
    flowEyebrow: "도면에서 공간까지", flowTitle: "인테리어 프로젝트의 시작을 더 명확하게", flowBody: "구조를 먼저 시각화한 후 전문가와 프로젝트 관리로 이어집니다.",
    flowTitles: ["도면 업로드", "실제 치수 확인", "3D 공간 탐색"], flowDescriptions: ["PDF, JPG, PNG, WebP 도면을 사용합니다.", "건물 유형, 가로·세로, 천장고와 공간 구성을 확인합니다.", "모델을 회전해 보고 원본 도면을 프로젝트에 비공개 저장합니다."],
    navStudio: "3D 구조 미리보기", navSamples: "디자인 샘플", navProfessionals: "전문가", navWorkspace: "내 프로젝트", navMaterials: "자재·상품"
  },
  en: english, es: english, zh: english, ja: english
};
